import React from "react";
import { AmplitudeEventSource } from "@analytics/Amplitude/AmplitudeEvents";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import { CompleteTaskEvent, StartTaskTimerEvent } from "../AmplitudeTaskManagementEvents";
import { AssignTaskTagEvent, RemoveTaskTagEvent } from "../AmplitudeTaskTagEvents";
import { useTaskListContext } from "../TaskListContext";
import { draggedTaskEvent$, selectedDetail$ } from "../TaskManagementGlobals";
import { ClientRecurringTask } from "../TaskManagementTypes";
import { useRecurringTaskMethods } from "../useRecurringTaskMethods";
import TaskItem from "./TaskItem";

export interface RecurringTaskItemProps {
    /**
     * Task item
     */
    readonly task: ClientRecurringTask;
    /**
     * Callback on key press
     */
    readonly onKeyPress: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
    /**
     * Indicates if task title field should be focused on
     */
    readonly isTaskInFocus: boolean;
    /**
     * Tell Amplitude where the event is triggered from
     */
    readonly amplitudeEventSource: AmplitudeEventSource;
}

export function RecurringTaskItem(props: RecurringTaskItemProps) {
    const amplitudeService = AmplitudeService.getInstance();
    const { taskFocusEvent$ } = useTaskListContext();
    const { overrideStatus, overrideTitle, overrideTag, deleteOneInstance, deleteAllInstances } =
        useRecurringTaskMethods();

    return (
        <TaskItem
            isTaskInFocus={props.isTaskInFocus}
            recurIcon={true}
            task={props.task}
            taskMethods={{
                buildKey: () => `${props.task.masterId}-${props.task.index}`,
                getDndItem: () => ({
                    type: "recurring-task-item",
                    data: props.task,
                }),
                updateTaskTitle: (title) =>
                    overrideTitle({
                        masterId: props.task.masterId,
                        index: props.task.index,
                        title,
                    }),
                updateTaskTag: (tagId) =>
                    overrideTag({
                        masterId: props.task.masterId,
                        index: props.task.index,
                        tagId,
                    }),
                updateTaskStatus: (status) =>
                    overrideStatus({
                        masterId: props.task.masterId,
                        index: props.task.index,
                        status,
                    }),
            }}
            events={{
                onClick: () => {
                    selectedDetail$.value = {
                        type: "recurring",
                        masterId: props.task.masterId,
                        index: props.task.index,
                    };
                },
                onFocus: () => {
                    taskFocusEvent$.value = {
                        type: "recurring",
                        masterId: props.task.masterId,
                        index: props.task.index,
                    };
                },
                onKeyPress: props.onKeyPress,
                onDrag: () => {
                    draggedTaskEvent$.value = {
                        type: "recurring",
                        masterId: props.task.masterId,
                        index: props.task.index,
                    };
                },
                onComplete: () => {
                    amplitudeService.logEvent(
                        new CompleteTaskEvent({
                            type: "recurring",
                            masterId: props.task.masterId,
                            index: props.task.index,
                            source: props.amplitudeEventSource,
                        }),
                    );
                },
                onTagAssign: (tagId) => {
                    amplitudeService.logEvent(
                        new AssignTaskTagEvent({
                            taskType: "recurring",
                            masterId: props.task.masterId,
                            index: props.task.index,
                            tagId,
                            source: "task-item",
                        }),
                    );
                },
                onTagRemove: () => {
                    amplitudeService.logEvent(
                        new RemoveTaskTagEvent({
                            taskType: "recurring",
                            masterId: props.task.masterId,
                            index: props.task.index,
                            source: "task-item",
                        }),
                    );
                },
                onTimerStart: (duration) => {
                    amplitudeService.logEvent(
                        new StartTaskTimerEvent({
                            source: props.amplitudeEventSource,
                            taskDuration: duration,
                            taskId: `${props.task.masterId}-${props.task.index}`,
                        }),
                    );
                },
            }}
            contextMenu={[
                {
                    key: "edit",
                    label: "Edit",
                    onClick: () => {
                        selectedDetail$.value = {
                            type: "recurring",
                            masterId: props.task.masterId,
                            index: props.task.index,
                        };
                    },
                },
                {
                    key: "delete-one",
                    label: "Delete just this task",
                    onClick: () => {
                        deleteOneInstance({
                            masterId: props.task.masterId,
                            index: props.task.index,
                        });
                    },
                },
                {
                    key: "delete-all",
                    label: "Delete all recurring tasks",
                    onClick: () => {
                        deleteAllInstances({
                            masterId: props.task.masterId,
                        });
                    },
                },
            ]}
        />
    );
}
